import { Upload } from 'antd';
import type { RcFile } from 'antd/es/upload';
import axios from 'axios';
import type { TFunction } from 'i18next';
import { useState } from 'react';

import { workflowsApi } from '@/api/workflows';
import { message } from '@/lib/message';

import { extractApiError } from '../statusUtils';

type Language = 'en' | 'fr';

type UseEntryUploadParams = {
  templateId: string;
  stepId: string;
  language: Language;
  /** i18n namespace; used to resolve `${ns}.uploadedN` and `${ns}.uploadFailed`. */
  i18nNs: string;
  /** Per-file MIME-type guard. Failing files are skipped with an i18n error toast. */
  isFileAccepted: (file: RcFile) => boolean;
  /** Full i18n key for the "wrong file type" error toast. */
  invalidTypeKey: string;
  /** Called once after a successful batch so the parent can refetch the workflow. */
  onChanged: () => Promise<void>;
  t: TFunction;
};

type UseEntryUploadResult = {
  uploading: boolean;
  /**
   * antd `beforeUpload` handler. Returns `Upload.LIST_IGNORE` so antd doesn't manage
   * the files itself — we do the upload + entry-creation per file in a batch.
   */
  beforeUpload: (file: RcFile, fileList: RcFile[]) => false | string;
};

/**
 * Shared upload-flow hook for file-bearing step drawers (PdfFileUpload).
 * Owns the `uploading` state plus the upload-then-addEntry flow so each drawer just
 * supplies its MIME filter and i18n namespace.
 *
 * Triggers the batch directly inside `beforeUpload` (not via an effect) so React StrictMode's
 * double-cleanup can't strand the spinner; `uploading` is unconditionally reset in `finally`.
 */
export function useEntryUpload({
  templateId,
  stepId,
  language,
  i18nNs,
  isFileAccepted,
  invalidTypeKey,
  onChanged,
  t,
}: UseEntryUploadParams): UseEntryUploadResult {
  const [uploading, setUploading] = useState(false);

  const uploadOne = async (file: RcFile) => {
    // One round trip: the bytes go to the API, which writes them to S3 and hands back the key
    // (ADR-0028). The BE owns the size cap (StorageOptions.MaxUploadBytes) and checks it against
    // the real body length, echoing the cap on rejection so the message below needs no hardcoded
    // number. There is no content-type dance any more — the presigned era required the PUT header
    // to match the signed type byte for byte, and there is no signature now.
    const { key } = await workflowsApi.uploadFile(templateId, file);
    await workflowsApi.addFileEntry(templateId, stepId, {
      label: fileLabel(file.name),
      enFileKey: language === 'en' ? key : null,
      frFileKey: language === 'fr' ? key : null,
    });
  };

  const runBatch = async (batch: RcFile[]) => {
    if (batch.length === 0) return;
    setUploading(true);
    let succeeded = 0;
    try {
      for (const file of batch) {
        if (!isFileAccepted(file)) {
          message.error(t(invalidTypeKey));
          continue;
        }
        try {
          await uploadOne(file);
          succeeded += 1;
        } catch (perFileErr) {
          // Per-file error handling so a single bad file (too large, rate limited)
          // doesn't abort the rest of the batch.
          const status = axios.isAxiosError(perFileErr) ? perFileErr.response?.status : undefined;
          if (status === 400) {
            const data = axios.isAxiosError(perFileErr) ? perFileErr.response?.data : undefined;
            const maxBytes = (data as { maxUploadBytes?: number } | undefined)?.maxUploadBytes;
            message.error(
              maxBytes
                ? t('workflow.steps.fileEntries.tooLarge', {
                    mb: Math.round(maxBytes / (1024 * 1024)),
                  })
                : (extractApiError(perFileErr).message ?? t(`${i18nNs}.uploadFailed`)),
            );
          } else if (status === 429) {
            message.error(t('workflow.steps.fileEntries.rateLimited'));
          } else {
            console.error('[useEntryUpload] upload failed', perFileErr);
            message.error(extractApiError(perFileErr).message ?? t(`${i18nNs}.uploadFailed`));
          }
        }
      }
      if (succeeded > 0) {
        message.success(t(`${i18nNs}.uploadedN`, { count: succeeded }));
        await onChanged();
      }
    } finally {
      // Unconditional — never leave the dropzone stuck in the spinner state.
      setUploading(false);
    }
  };

  // antd calls beforeUpload once per file in a multi-select batch; trigger one runBatch
  // on the first call so we get a single toast + onChanged per pick.
  const beforeUpload = (file: RcFile, fileList: RcFile[]) => {
    if (file === fileList[0]) {
      void runBatch(fileList);
    }
    return Upload.LIST_IGNORE;
  };

  return { uploading, beforeUpload };
}

/**
 * Initial entry label from the uploaded filename. PDF uploads keep the `.pdf`
 * suffix so the list reads as actual filenames; any other extension is stripped
 * (the hook is currently PDF-only but the helper stays generic in case the
 * reference-materials drawer wires up later).
 */
export function fileLabel(filename: string): string {
  const trimmed = filename.trim();
  if (!trimmed) return filename;
  if (/\.pdf$/i.test(trimmed)) {
    // Normalise the case so "NAAF_2026.PDF" reads as "NAAF_2026.pdf".
    return `${trimmed.slice(0, -4)}.pdf`;
  }
  return trimmed.replace(/\.[^.]+$/, '') || trimmed;
}
